import React from 'react';
import Div from '~/components/atom/Div';
import Divider from '~/components/atom/Divider';
import MultiSelectInput from '~/components/molecule/MultiSelectInput';
import { TEXT, NO_OFFICE_ID, NO_USER_ID } from '../../constants';
import SectionContainer from '../SectionContainer';
import SectionHeaderWithDescription from '../SectionHeaderWithDescription';
import type {
  UpdateSettingsAction,
  UpdateSettingsState,
} from '../../reducers/settingsReducer';
import useOfficeOptions from '~/hooks/useOfficeOptions';
import useUserOptions from '~/hooks/useUserOptions';
import getAddressString from '~/components/page/LFA/utils/getAddressString';
import { CountryCode } from '~/graphql/types';

const NO_OFFICE_OPTION = {
  label: TEXT.noOfficeLabel,
  key: NO_OFFICE_ID,
  payload: {
    id: NO_OFFICE_ID,
    filterLabel: TEXT.noOfficeLabel,
    tagLabel: TEXT.noOfficeLabel,
  },
};

const NO_USER_OPTION = {
  label: TEXT.noUserLabel,
  key: NO_USER_ID,
  payload: {
    id: NO_USER_ID,
    filterLabel: TEXT.noUserLabel,
    tagLabel: TEXT.noUserLabel,
  },
};

type Props = {
  updateSettings: UpdateSettingsState;
  dispatch: React.Dispatch<UpdateSettingsAction>;
};

const UserOfficeSelectionSection: React.FCC<Props> = ({
  updateSettings,
  dispatch,
}) => {
  const baseOfficeOptions = useOfficeOptions();
  const baseUserOptions = useUserOptions();

  const officeOptions = [
    NO_OFFICE_OPTION,
    ...baseOfficeOptions.map(option => ({
      label: option.label,
      key: option.key,
      payload: {
        id: option.key,
        filterLabel: option.label + ' (ID: ' + option.key + ')',
        tagLabel:
          option.label +
          (option.payload?.address
            ? ` (${getAddressString({ ...option.payload?.address, countryCode: CountryCode.Nl })})`
            : ` (${TEXT.noAddressFound})`),

        address: option.payload?.address,
      },
    })),
  ];

  const userOptions = [
    NO_USER_OPTION,
    ...baseUserOptions.map(option => ({
      label: option.label,
      key: option.key,
      payload: {
        id: option.key,
        filterLabel: option.label,
        tagLabel: option.label,
      },
    })),
  ];

  return (
    <SectionContainer direction="row" justification="space-between" gap="base">
      <Div width="50%">
        <SectionHeaderWithDescription
          icon="office"
          header={TEXT.officeInputHeader}
          description={TEXT.officeInputDescription}
        />
        <MultiSelectInput
          options={officeOptions}
          selectedOptions={officeOptions.filter(option =>
            updateSettings?.rules?.notOfficeId.includes(
              option.payload.id.toString(),
            ),
          )}
          onSelected={option =>
            dispatch({ type: 'ADD_NOT_OFFICE', payload: option.payload.id })
          }
          onRemoved={option =>
            dispatch({
              type: 'REMOVE_NOT_OFFICE',
              payload: option.payload.id,
            })
          }
          width="100%"
          placeholder={TEXT.selectOfficesPlaceholder}
        />
      </Div>
      <Divider margin={[null, 's']} vertical />

      <Div width="50%">
        <SectionHeaderWithDescription
          icon="user"
          header={TEXT.userInputHeader}
          description={TEXT.userInputDescription}
        />
        <MultiSelectInput
          options={userOptions}
          selectedOptions={userOptions.filter(option =>
            updateSettings?.rules?.notUserId.includes(
              option.payload.id.toString(),
            ),
          )}
          onSelected={option =>
            dispatch({ type: 'ADD_NOT_USER', payload: option.payload.id })
          }
          onRemoved={option =>
            dispatch({
              type: 'REMOVE_NOT_USER',
              payload: option.payload.id,
            })
          }
          width="100%"
          placeholder={TEXT.selectUsersPlaceholder}
        />
      </Div>
    </SectionContainer>
  );
};

export default UserOfficeSelectionSection;
